'use client';

import React from 'react';

interface DisplayCardProps {
    className?: string;
    icon?: React.ReactNode;
    title?: string;
    description?: string;
    date?: string;
}

function DisplayCard({ className, icon, title = 'Featured', description = 'Discover amazing content' }: DisplayCardProps) {
    return (
        <div
            className={
                "display-card group relative flex dark:bg-black dark:border-gray-700 h-24 w-64 lg:-skew-y-[8deg] md:-skew-y-[8deg] -skew-y-[0] select-none flex-col gap-1.5 rounded-xl border-2 bg-muted/70 backdrop-blur-lg px-2.5 py-2 transition-all duration-700 before:absolute before:inset-0 before:bg-gradient-to-r before:from-gray-200/30 before:to-white before:rounded-xl before:content-[''] after:absolute after:-right-1 after:top-[-5%] after:h-[110%] after:w-full after:bg-gradient-to-l dark:before:from-black/90 dark:before:to-black/90     after:from-background after:to-transparent after:content-[''] dark:after:from-black dark:after:to-transparent [&>*]:flex [&>*]:items-left [&>*]:gap-2 [&>*]:relative [&>*]:z-10 sm:h-[140px] sm:w-full sm:gap-3 sm:px-4 sm:py-3 md:h-36 md:w-[22rem] md:gap-4 sm:mb-5 md:mb-0 lg:mb-0 xl:mb-0 " +
                (className || '')
            }
        >
            <div className="flex items-center">
                <span className="relative inline-block rounded-full bg-[#111] p-1 transition-all sm:p-2 dark:bg-secondary dark:bg-opacity-20">
                    {icon}
                </span>
                <p className="text-sm font-semibold text-[#111] transition-colors mb-0 sm:text-lg dark:text-white">{title}</p>
            </div>
            <p className="mb-0 text-xs font-semibold text-secondary sm:text-base dark:text-light">{description}</p>
        </div>
    );
}

interface DisplayCardsProps {
    cards?: DisplayCardProps[];
}

function DisplayCards({ cards }: DisplayCardsProps) {
    const defaultCards: DisplayCardProps[] = [
        {
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    fill="none"
                    className="sm:w-6 sm:h-6"
                >
                    <path
                        d="M17.4323 5.38477C17.5859 5.44933 17.7742 5.5304 17.9889 5.62793C18.6073 5.90886 19.4397 6.31933 20.3131 6.83398C21.1886 7.34989 22.091 7.96217 22.8571 8.64551C23.4941 9.21382 24.0081 9.8102 24.3463 10.4141C22.8184 12.3859 22.227 13.2115 21.985 13.5967L9.22815 43.4141C8.60299 44.9741 6.8838 46.0254 5.12268 46.0254C4.57114 46.0254 4.06461 45.9062 3.42932 45.6953C2.36688 45.2765 1.91715 44.5496 1.80042 43.6523C1.67503 42.6885 1.93964 41.5019 2.44006 40.3223L16.9049 6.5957C17.0582 6.2168 17.6666 5.58105 17.4323 5.38477Z"
                        stroke="white"
                        strokeWidth="1.5"
                    />
                    <path
                        d="M31.2169 5.11914C31.2953 5.19758 31.4315 5.33373 31.5021 5.4043L46.6339 40.3252C47.1336 41.5039 47.3988 42.6892 47.2735 43.6523C47.1569 44.5488 46.707 45.2744 45.6466 45.6934C45.0099 45.9048 44.5027 46.0254 43.9503 46.0254C42.2992 46.0253 40.6849 45.1012 39.9786 43.708L26.7062 13.0088C25.3138 10.5575 22.9321 6.59647 17.9015 4.9082C20.1485 3.97579 21.2619 3.90039 22.2882 3.90039H28.1349C28.6876 3.97559 30.296 4.4834 31.2169 5.11914Z"
                        stroke="white"
                        strokeWidth="1.5"
                    />
                </svg>
            ),
            title: 'Assembly AI',
            description: 'Real-time speech-to-text and audio analysis',
            className:
                '[grid-area:stack] translate-x-1 -translate-y-1 hover:-translate-y-4 grayscale-[100%] hover:grayscale-0 transition-all sm:translate-x-0 sm:-translate-y-0 sm:hover:-translate-y-[0] md:translate-x-4 md:-translate-y-3 md:hover:-translate-y-20',
        },
        {
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    fill="none"
                    className="sm:w-6 sm:h-6"
                >
                    <path d="M35.3461 7.75V42.25H29.4486V7.75H35.3461Z" stroke="white" strokeWidth="1.5" />
                    <path d="M20.5516 7.75V42.25H14.6541V7.75H20.5516Z" stroke="white" strokeWidth="1.5" />
                </svg>
            ),
            title: 'Eleven Labs',
            description: 'Neural TTS for hyper-realistic voice generation',
            className:
                '[grid-area:stack] translate-x-4 translate-y-2 hover:-translate-y-2 grayscale-[100%] hover:grayscale-0 transition-all sm:translate-x-0 sm:translate-y-0 sm:hover:-translate-y-[0] md:translate-x-16 md:translate-y-10 md:hover:-translate-y-8',
        },
        {
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    fill="none"
                    className="sm:w-6 sm:h-6"
                >
                    <g>
                        <path
                            d="M47.3717 19.47L46.151 30.9281C45.8404 33.7454 44.7392 36.0191 43.0201 37.5882C41.3031 39.1554 38.9108 40.0736 35.9146 40.0736H11.1334L11.8277 33.0218H35.5406C36.4471 33.0218 37.2606 32.773 37.8746 32.224C38.4877 31.6755 38.8187 30.9027 38.9039 30.0169L40.0465 19.47H47.3717ZM14.6051 10.9183H39.3863L38.693 17.97H14.9791C14.0725 17.9701 13.2591 18.2197 12.6451 18.7689C12.032 19.3175 11.7019 20.09 11.6168 20.9759L10.4742 31.5218H3.14902L4.36874 20.0638C4.67939 17.2467 5.78059 14.9727 7.4996 13.4036C9.21658 11.8364 11.6091 10.9183 14.6051 10.9183Z"
                            stroke="white"
                            strokeWidth="1.5"
                        />
                    </g>
                </svg>
            ),
            title: 'HeyGen, Synthesia',
            description: 'AI avatars and generative video creation',
            className:
                '[grid-area:stack] translate-x-8 translate-y-5 hover:translate-y-2 sm:translate-x-0 sm:translate-y-0 sm:hover:translate-y-[0] md:translate-x-32 md:translate-y-20 md:hover:translate-y-10',
        },
        
    ];

    const displayCards = cards || defaultCards;

    return (
        <div className="block sm:block md:grid lg:grid xl:grid [grid-template-areas:'stack'] place-items-start opacity-100 animate-in fade-in-0 duration-700 min-h-[200px] sm:min-h-[300px] md:min-h-[300px] md:ml-24 sm:ml-0 lg:ml-0 xl:ml-0">
            {displayCards.map((cardProps, index) => (
                <DisplayCard key={index} {...cardProps} />
            ))}
        </div>
    );
}

const IntegrationsPlatforms = () => {
    return (
        <div className="py-8 sm:py-12 md:py-16">
            <div className="container xl:container mx-auto">
                {/* Side by side layout with responsive design */}
                <div className="row grid lg:grid-cols-12">
                    {/* Text Content Section */}
                    <div className="lg:col-span-6 mb-5">
                        <h2 className="mb-4 text-2xl font-bold sm:text-3xl md:text-4xl dark:text-white">Integrations</h2>
                        <p
                            className="dark:text-[#D7DEE5] mb-0 text-sm font-semibold text-gray-600 sm:text-base md:text-lg"
                            // data-aos="fade-up"
                            // data-aos-duration="700"
                            // data-aos-delay="200"
                        >
                            We seamlessly integrate with leading AI and media platforms to support dynamic content generation,
                            speech synthesis, video personalization, and multimodal AI experiences.
                        </p>
                    </div>

                    {/* Display Cards Section */}
                    <div className="lg:col-span-6">
                        <DisplayCards />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntegrationsPlatforms;
